import BooleanSelect from 'client/components/core/Sidebar/components/Settings/components/BooleanNew'
import NewCheckbox from 'client/components/core/Sidebar/components/Settings/components/NewCheckbox'
import {
  useUpdateEntity,
  useUpdateProp,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import HtmlAttrId from 'client/components/core/Sidebar/components/Settings/options/HtmlAttrId'
import BorderStyle from 'client/components/core/Sidebar/components/Settings/styles/Border'
import ShadowStyle from 'client/components/core/Sidebar/components/Settings/styles/ShadowStyle'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { commonModalWidth } from 'common/constants/commonWidth'
import { MAX_CLOSED_POPUP_DAYS } from 'common/constants/popup'
import { PopupInterface } from 'common/types/entities/popup-interface'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { SizeAndPositionGroup } from 'src/editor/shared/settings/groups/size-and-position'
import { RangeElement } from 'src/editor/shared/ui-lib/range-element'

interface PopupSettingsProps {
  entity: PopupInterface
}

export const PopupSettingsNew = ({ entity }: PopupSettingsProps) => {
  const { t } = useTranslation()
  const updateProp = useUpdateProp(entity)
  const updateEntity = useUpdateEntity<PopupInterface>()
  const { isMobile } = useDeviceModeContext()

  const toggleMobileOpenAutomatically = (value: boolean) => {
    updateEntity({ ...entity, openOnMobileAutomatically: Boolean(value) })
  }

  const toggleOpenOnExit = (value: boolean) => {
    updateEntity({
      ...entity,
      openOnExit: Boolean(value),
      openOnMobileAutomatically: Boolean(value),
    })
  }

  const toggleDelayOption = (value: boolean) => {
    updateEntity({
      ...entity,
      closedDays: value ? 1 : undefined,
    })
  }

  return (
    <>
      <BooleanSelect
        labelText="entity_options.show_close_button.title"
        value={entity.showClose}
        update={updateProp('showClose')}
      />
      {entity.showClose && (
        <ColorPickerControl
          label="entity_settings.popup.color_button_close"
          color={entity.closeButtonColor}
          mobileColor={entity.closeButtonMobileColor}
          update={updateProp('closeButtonColor')}
          mobileUpdate={updateProp('closeButtonMobileColor')}
          isClearable
        />
      )}
      {entity.showClose && (
        <NewCheckbox
          labelText="entity_settings.popup.delay.label"
          helpMessage="entity_settings.popup.delay.help_message"
          checked={!!entity.closedDays}
          update={toggleDelayOption}
        />
      )}
      {!!entity.closedDays && (
        <RangeElement
          label={t('entity_settings.popup.reopen_in_x_days_icu', {
            days: entity.closedDays || 0,
          })}
          value={entity.closedDays}
          onChange={updateProp('closedDays')}
          min={1}
          max={MAX_CLOSED_POPUP_DAYS}
          allowNegative={false}
        />
      )}
      <BooleanSelect
        labelText="entity_settings.popup.open_automatically"
        value={entity.openAutomatically}
        update={updateProp('openAutomatically')}
      />
      {entity.openAutomatically && (
        <RangeElement
          label={t('entity_settings.popup.open_in_x_seconds_icu', {
            seconds: entity.delay,
          })}
          value={entity.delay}
          onChange={updateProp('delay')}
          min={0}
          max={120}
          allowNegative={false}
        />
      )}
      <BooleanSelect
        labelText="entity_settings.popup.open_on_exit"
        value={entity.openOnExit}
        update={toggleOpenOnExit}
      />
      {entity.openOnExit && (
        <NewCheckbox
          labelText="entity_settings.popup.mobile_open_on_exit"
          checked={entity.openOnMobileAutomatically}
          update={toggleMobileOpenAutomatically}
        />
      )}
      {!isMobile && (
        <RangeElement
          label="width"
          value={entity.width || commonModalWidth}
          onChange={updateProp('width')}
          min={300}
          max={1200}
          allowNegative={false}
        />
      )}
      {entity.openOnMobileAutomatically && (
        <RangeElement
          label={t('entity_settings.popup.open_in_x_seconds_icu', {
            seconds: entity.mobileDelay,
          })}
          value={entity.mobileDelay}
          onChange={updateProp('mobileDelay')}
          min={0}
          max={120}
          allowNegative={false}
        />
      )}
      <GroupTitle>color</GroupTitle>
      <ColorPickerControl
        isGradientAvailable
        label="entity_settings.popup.background"
        color={entity.background}
        mobileColor={entity.mobileBackground || entity.background}
        update={updateProp('background')}
        mobileUpdate={updateProp('mobileBackground')}
        isClearable
      />
      <SizeAndPositionGroup
        padding={isMobile ? entity?.mobilePadding : entity.padding}
        updatePadding={updateProp(isMobile ? 'mobilePadding' : 'padding')}
      />
      <BorderStyle
        showGroupTitle
        border={entity.border}
        mobileBorder={entity.mobileBorder}
        update={updateProp('border')}
        mobileUpdate={updateProp('mobileBorder')}
      />
      <ShadowStyle
        shadow={entity.boxShadow}
        mobileShadow={entity.mobileBoxShadow}
        update={updateProp('boxShadow')}
        mobileUpdate={updateProp('mobileBoxShadow')}
      />
      <HtmlAttrId attrId={entity.htmlAttrId} />
    </>
  )
}
